import React from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { useWishlistContext } from '@/account/context/wishlist/index';
import { goToMyAccountWishlist } from '@/utils/Common/Redirects';
import GigyaLoginPopupController from '@/utils/Controllers/Gigya/GigyaLoginPopupController';
import LINK from '@/utils/Constants/Link';

// import api
import isAuth from '@/api/get/isAuth';

// import hooks
import useFetchWishlist from '@/hooks/useFetchWishlist';

// import components
import Title from '@/components/atoms/Title';
import Button from '@/components/atoms/Button';
import ALink from '@/components/atoms/ALink';
import WishlistEmpty from '@/components/molecules/WishlistEmpty';
import WishlistProductWrapper from '@/components/organisms/WishlistProductWrapper';
import useFirstRenderReveal from '@/hooks/useFirstRenderReveal';

const WishlistGuestApp = ({ sharedLink }) => {
  const { t } = useTranslation();
  const [{ entries, entriesLoading }] = useWishlistContext();
  const wrapperRef = useFirstRenderReveal(entriesLoading);

  const showLoginPopup = async () => {
    const isLogged = await isAuth();
    if (!isLogged) {
      GigyaLoginPopupController.init({
        successCB: goToMyAccountWishlist,
        header: t('wishlist:wishlist.page.login.popup.header'),
        subHeader: t('wishlist:wishlist.page.login.popup.subheader'),
      });
    }
  };

  // hooks
  useFetchWishlist({ sharedLink });

  if (entriesLoading) return null;

  return (
    <div
      ref={wrapperRef}
      style={{ opacity: 0 }}
      className={`dln-wishlist dln-page dln-wishlist--public${
        sharedLink ? ' dln-wishlist--shared' : ''
      }`}
    >
      <div className='dln-wrapper'>
        <div className='dln-wishlist__content dln-page__content'>
          <Title
            text={t(
              `wishlist:${
                sharedLink
                  ? 'wishlist.page.shared.title'
                  : 'wishlist.page.title'
              }`,
            )}
          />
          {_isEmpty(entries) && (
            <WishlistEmpty
              subtitle={t('wishlist:wishlist.page.empty.header')}
              paragraph={t('wishlist:wishlist.page.empty.paragraph')}
              ctaText={t('button.go.home.text')}
            />
          )}
          {sharedLink && !_isEmpty(entries) && (
            <h2 className='dln-wishlist__text'>
              {t('wishlist:wishlist.page.shared.subtitle')}
            </h2>
          )}
          {!_isEmpty(entries) && (
            <>
              {!sharedLink && (
                <div className='dln-wishlist__signInNotice'>
                  <Button
                    modifiers={['link']}
                    onClick={showLoginPopup}
                    text={t('wishlist:wishlist.page.guest.sign.cta')}
                  />{' '}
                  {t(
                    'wishlist:wishlist.page.guest.sign.register.connective.label',
                  )}{' '}
                  <ALink
                    text={t('wishlist:wishlist.page.guest.register.cta')}
                    url={LINK.REGISTER}
                  />{' '}
                  {t('wishlist:wishlist.page.guest.sign.register.label')}
                </div>
              )}
              <div className='dln-wishlist__productWrap'>
                <WishlistProductWrapper
                  entries={entries}
                  hasDelete={!sharedLink}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

WishlistGuestApp.propTypes = {
  sharedLink: PropTypes.string,
};

WishlistGuestApp.defaultProps = {
  sharedLink: null,
};

export default WishlistGuestApp;
